import { useCallback, useEffect, useState } from 'react';
import type { Map as CarteGL } from 'maplibre-gl';
import {
  denominateurEchelle, ECHELLES_NORMALISEES, formaterDistance, palierEchelle, versUtm,
  zoomPourEchelle,
} from '../../src/partage/utm.js';

/**
 * MÈTRES PAR PIXEL AU CENTRE DE LA VUE.
 *
 * ⚠️ AU CENTRE, PAS À L'ÉQUATEUR. La formule scolaire
 * `156543 · cos(lat) / 2^z` vaut pour la latitude regardée ; l'appliquer avec
 * la latitude zéro donnerait une échelle fausse de 1,4 % à Conakry — soit
 * 1,4 m sur une parcelle de 100 m. On mesure donc réellement, en déprojetant
 * deux pixels voisins.
 *
 * ⚠️⚠️ HORS DU COMPOSANT, ET C'EST LE POINT. L'affichage de l'échelle et le
 * VERROU d'échelle doivent lire la même mesure. Deux mesures de la même chose
 * divergent au premier correctif appliqué à une seule — et ici la divergence
 * serait muette : la barre annoncerait 1/2 000 pendant que le verrou tiendrait
 * autre chose, sans que rien ne rougisse.
 */
function mesurerMetresParPixel(carte: CarteGL): number {
  const c = carte.getCenter();
  const a = carte.unproject([0, 0]);
  const b = carte.unproject([1, 0]);
  const dlon = (b.lng - a.lng) * 111_320 * Math.cos((c.lat * Math.PI) / 180);
  const dlat = (b.lat - a.lat) * 110_574;
  const mpp = Math.hypot(dlon, dlat);
  return Number.isFinite(mpp) && mpp > 0 ? mpp : 1;
}

/** Deux échelles sont « la même » à un millième près — en deçà, on ne corrige plus. */
const MEME_ECHELLE = 0.001;

/**
 * ⚠️ LE TIRET EST UNE VALEUR, PAS UNE ABSENCE — item D.944. Chaque clé de la
 * barre est présente AU CHARGEMENT ; une valeur qu'on ne sait pas encore dire
 * s'écrit « — », jamais rien. Une clé qui disparaît quand sa valeur manque se
 * lit « cette information n'existe pas sur ce poste », et l'agent ne la
 * cherchera plus jamais.
 */
const TIRET = '—';

/**
 * LA BARRE D'ÉTAT DU POSTE — les sept valeurs, en permanence (D.944).
 *
 * coordonnées · échelle · unité (fuseau) · outil · accrochage · mesure ·
 * sélection · opposabilité — plus la POSITION (la collectivité sous le centre,
 * item D.997 : c'est ici qu'elle s'affiche, jamais dans l'inspecteur, parce
 * qu'elle bouge avec la carte et qu'une sélection ne doit bouger qu'au geste)
 * et les avertissements de seuils qui expliquent une carte vide.
 *
 * ⚠️⚠️ CE QUI SÉPARE UN ÉDITEUR CADASTRAL D'UNE CARTE WEB. Un géomètre ne
 * travaille pas en degrés : il travaille en mètres, dans un fuseau nommé, à une
 * échelle qu'il peut citer. Une carte qui affiche « 9.5100, -13.7100 » et rien
 * d'autre est lisible par un développeur et inutilisable par un professionnel.
 *
 * ⚠️ LE FUSEAU EST AFFICHÉ AVEC LES COORDONNÉES, JAMAIS SÉPARÉ. La Guinée est
 * coupée par le méridien 12°W : Conakry est en zone 28, Kankan en zone 29. Un
 * X sans son fuseau donne un nombre qui SEMBLE comparable à un autre et ne
 * l'est pas.
 *
 * ⚠️ PAS DE `role="status"` SUR LA BARRE. Elle change à chaque mouvement de
 * souris : une région live ici ferait annoncer chaque coordonnée à un lecteur
 * d'écran — le poste deviendrait inutilisable à VoiceOver dès le premier
 * survol. Seul le REFUS du verrou s'annonce (`role="alert"`), parce qu'il est
 * rare et qu'il dit qu'un geste a échoué.
 *
 * ⚠️ LA PROJECTION EST CELLE DE `src/partage/utm.ts`, ÉPROUVÉE CONTRE POSTGIS
 * (`tests/utm.test.ts`, huit points réels, refus au-delà du millimètre).
 */
export function ReperesCarte({
  carte, onEchelleVerrouillee,
  position = null, onAdopterPosition,
  outil = null, accroche = null, mesure = null, selection = null, opposabilite = null,
  voieSurvolee = null, outilMesure = null, actionSelection = null, verrouImpose = null,
  seuils, zoomNomsDeRue, zoomBatiComplet,
}: {
  carte: CarteGL | null;
  /**
   * L'échelle tenue, pour qui en dépend. Rendue au parent PARCE QUE l'outil
   * Planche doit savoir ce que l'agent a verrouillé — sans que la barre aille
   * chercher la planche, ni l'inverse.
   */
  onEchelleVerrouillee?: (denominateur: number | null) => void;
  /** La collectivité sous le centre — information de POSITION (D.997). */
  position?: { nom: string; echelon: string } | null;
  /** Adopter la position comme sélection — c'est un GESTE, donc un bouton. */
  onAdopterPosition?: () => void;
  /** L'outil actif de l'atelier — « — » quand on consulte. */
  outil?: string | null;
  /** Le dernier accrochage effectif, nommé — « — » hors tracé. */
  accroche?: string | null;
  /** La mesure courante (longueur ou périmètre) — « — » hors mesure. */
  mesure?: string | null;
  /** Ce qui est sélectionné — parcelle, bâtiment, section, collectivité. */
  selection?: string | null;
  /** Le statut juridique de l'objet sélectionné — jamais un booléen global. */
  opposabilite?: string | null;
  /** La voie sous le curseur — l'info de la bulle, aussi lisible ici (D.951). */
  voieSurvolee?: string | null;
  /**
   * LA MESURE — seul outil de la consultation (contrat §4). Ses commandes
   * vivent ICI, dockées : l'ancienne barre flottante est partie à l'atelier
   * (D.968), et mesurer n'est pas éditer.
   */
  outilMesure?: {
    actif: boolean; nature: 'ligne' | 'surface';
    demarrer: (n: 'ligne' | 'surface') => void; arreter: () => void;
  } | null;
  /** Une action offerte sur la sélection — ex. « ouvrir dans l'atelier ». */
  actionSelection?: { libelle: string; executer: () => void } | null;
  /**
   * Un verrou commandé de l'EXTÉRIEUR — « tenir l'écran à l'échelle de la
   * planche » (D.945). Adopté sur sa clé, comme toute valeur imposée de ce
   * dépôt : la même échelle re-demandée doit quand même s'appliquer. Il passe
   * par `poser`, donc par les MÊMES refus qu'un choix de la main.
   */
  verrouImpose?: { cle: string; denominateur: number | null } | null;
  /** Les seuils de zoom servis — pour dire pourquoi une couche est absente. */
  seuils?: { parcelles: number; batiments: number } | null;
  zoomNomsDeRue?: number;
  zoomBatiComplet?: number;
}) {
  const [curseur, setCurseur] = useState<{ lon: number; lat: number } | null>(null);
  const [vue, setVue] = useState<{ metresParPixel: number; zoom: number } | null>(null);
  const [inclinaison, setInclinaison] = useState(0);
  const [verrouEchelle, setVerrouEchelle] = useState<number | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  useEffect(() => {
    if (!carte) return;

    const mesurer = () => setVue({
      metresParPixel: mesurerMetresParPixel(carte),
      zoom: carte.getZoom(),
    });

    const surMouvement = (e: { lngLat: { lng: number; lat: number } }) =>
      setCurseur({ lon: e.lngLat.lng, lat: e.lngLat.lat });
    /* ⚠️ LE CURSEUR S'EFFACE QUAND IL SORT. Laisser la dernière position
       affichée ferait lire une coordonnée périmée comme une coordonnée
       courante — et un agent la recopierait. */
    const surSortie = () => setCurseur(null);
    /* ⚠️ L'INCLINAISON EST SUIVIE PARCE QU'UNE EXTRUSION VUE DU DESSUS N'EST
       PAS UNE EXTRUSION : à `pitch` nul MapLibre ne dessine que le toit. Les
       volumes sont calculés, envoyés, peints — et invisibles. Un dispositif
       qui ne se voit pas doit s'annoncer. */
    const surInclinaison = () => setInclinaison(Math.round(carte.getPitch()));

    carte.on('mousemove', surMouvement);
    carte.on('mouseout', surSortie);
    carte.on('move', mesurer);
    carte.on('pitch', surInclinaison);
    mesurer();

    return () => {
      carte.off('mousemove', surMouvement);
      carte.off('mouseout', surSortie);
      carte.off('move', mesurer);
      carte.off('pitch', surInclinaison);
    };
  }, [carte]);

  /**
   * POSER LE VERROU — ou REFUSER, et le dire.
   *
   * ⚠️ UN VERROU QUI NE PEUT PAS TENIR NE DOIT PAS SE POSER. Si l'échelle
   * demandée sort des bornes de zoom de la carte, MapLibre écrête en silence :
   * l'agent lirait « 1/200 » posé et regarderait du 1/380. On refuse, on nomme
   * le dénominateur atteignable, et le verrou reste ouvert. « Je ne peux pas »
   * et « c'est fait » ne doivent pas se ressembler.
   */
  const poser = useCallback((denominateur: number | null) => {
    setRefus(null);
    if (denominateur === null) { setVerrouEchelle(null); return; }
    if (!carte) { setRefus('La carte n’est pas encore prête.'); return; }

    const mpp = mesurerMetresParPixel(carte);
    const z = zoomPourEchelle(carte.getZoom(), mpp, denominateur);
    if (z === null) { setRefus('Échelle inapplicable : la vue n’est pas mesurable.'); return; }

    const zmin = carte.getMinZoom();
    const zmax = carte.getMaxZoom();
    if (z > zmax || z < zmin) {
      const atteignable = denominateurEchelle(mpp * 2 ** (carte.getZoom() - (z > zmax ? zmax : zmin)));
      setRefus(
        `1/${denominateur.toLocaleString('fr-FR')} sort des limites de la carte. `
        + `Au plus près : 1/${Math.round(atteignable).toLocaleString('fr-FR')}. Le verrou n’est pas posé.`);
      return;
    }

    setVerrouEchelle(denominateur);
    /* ⚠️ Durée NULLE — refus D.951, pas d'animation de caméra. La re-tenue
       sur `zoomend` ci-dessous utilisait déjà cette forme. */
    carte.easeTo({ zoom: z, duration: 0 });
  }, [carte]);

  /* L'échelle tenue est dite au parent — y compris sa levée. */
  useEffect(() => { onEchelleVerrouillee?.(verrouEchelle); }, [verrouEchelle, onEchelleVerrouillee]);

  /* Le verrou imposé, adopté sur sa clé. */
  const cleImposee = verrouImpose?.cle ?? null;
  useEffect(() => {
    if (!verrouImpose) return;
    poser(verrouImpose.denominateur);
  }, [cleImposee]);   // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * LE VERROU TIENT — quoi qu'il arrive au zoom.
   *
   * ⚠️ ON NEUTRALISE LES GESTES **ET** ON REMET L'ÉCHELLE. Neutraliser seul
   * laisserait passer le clavier, `fitBounds`, un saut d'un autre écran ;
   * remettre seul ferait lutter la molette contre la carte à chaque cran. Les
   * deux ensemble : le geste ne fait rien, et ce qui passe quand même est
   * corrigé.
   *
   * ⚠️ LA ROTATION TOMBE AUSSI, et c'est assumé : `touchZoomRotate` ne se
   * sépare pas, et un plan cadastral se lit nord en haut — c'est la convention
   * de la feuille délivrée.
   */
  useEffect(() => {
    if (!carte || verrouEchelle === null) return;

    const gestes = [carte.scrollZoom, carte.boxZoom, carte.doubleClickZoom, carte.touchZoomRotate];
    gestes.forEach((g) => g.disable());

    const remettre = () => {
      const mpp = mesurerMetresParPixel(carte);
      const ecart = Math.abs(denominateurEchelle(mpp) - verrouEchelle) / verrouEchelle;
      if (ecart < MEME_ECHELLE) return;          // déjà à l'échelle : ne pas boucler
      const z = zoomPourEchelle(carte.getZoom(), mpp, verrouEchelle);
      if (z !== null) carte.easeTo({ zoom: z, duration: 0 });
    };
    carte.on('zoomend', remettre);

    return () => {
      gestes.forEach((g) => g.enable());
      carte.off('zoomend', remettre);
    };
  }, [carte, verrouEchelle]);

  if (!vue) return null;

  const denominateur = denominateurEchelle(vue.metresParPixel);
  const barre = palierEchelle(vue.metresParPixel);
  const utm = curseur ? versUtm(curseur.lon, curseur.lat) : null;
  const zoom = vue.zoom;

  return (
    <footer className="reperes-carte" aria-label="Barre d’état du plan">
      {/* ── Coordonnées + unité ─────────────────────────────────────────
          ⚠️ DEUX LECTURES, PAS UNE. Le géodésique sert à recopier vers un GPS ;
          le projeté sert à mesurer et à reporter. N'en donner qu'une oblige
          l'agent à convertir de tête — ce qu'il fera faux un jour. */}
      <div className="reperes-ligne" title="Position du curseur sur le plan">
        <span className="reperes-cle">WGS84</span>
        <span className="reperes-valeur mono">
          {curseur ? `${curseur.lat.toFixed(6)}, ${curseur.lon.toFixed(6)}` : TIRET}
        </span>
      </div>
      <div className="reperes-ligne">
        <span className="reperes-cle">{utm ? `UTM ${utm.zone}N` : 'UTM'}</span>
        <span className="reperes-valeur mono">
          {utm
            ? `X ${Math.round(utm.x).toLocaleString('fr-FR')} · Y ${Math.round(utm.y).toLocaleString('fr-FR')} m`
            : TIRET}
        </span>
        {utm && <span className="reperes-epsg">EPSG:{utm.epsg}</span>}
      </div>

      {/* ── Échelle, verrou, barre graphique ──────────────────────────── */}
      <div className="reperes-ligne reperes-echelle">
        <span className="reperes-cle">Échelle</span>
        {/* ⚠️ LA VALEUR AFFICHÉE EST TOUJOURS LA MESURÉE, JAMAIS LA DEMANDÉE.
            Afficher le dénominateur verrouillé ferait dire à la barre ce qu'on
            lui a ordonné au lieu de ce qu'elle voit : si le verrou lâchait, la
            barre continuerait de l'affirmer. Le verrou dit l'intention, le
            nombre dit le fait. */}
        <span className="reperes-valeur">1 / {Math.round(denominateur).toLocaleString('fr-FR')}</span>
        {verrouEchelle !== null && (
          /* ⚠️ EN TOUTES LETTRES, pas un cadenas muet : le sens « tenue » ne
             vivait que dans un `title` — une infobulle exclusive, refus D.951. */
          <span className="reperes-verrou-marque">
            tenue 1/{verrouEchelle.toLocaleString('fr-FR')}
          </span>
        )}
        <label className="reperes-cle" htmlFor="reperes-verrou-choix">Tenir</label>
        <select id="reperes-verrou-choix"
                className="reperes-verrou-choix"
                value={verrouEchelle === null ? '' : String(verrouEchelle)}
                onChange={(e) => poser(e.target.value === '' ? null : Number(e.target.value))}>
          <option value="">libre</option>
          {ECHELLES_NORMALISEES.map((d) => (
            <option key={d} value={d}>1 / {d.toLocaleString('fr-FR')}</option>
          ))}
        </select>
      </div>
      {/* ⚠️ Le rapport d'échelle suppose une densité de pixel de référence
          (0,28 mm, convention OGC) ; l'écran réel de l'agent n'y est pas. La
          barre GRAPHIQUE, elle, reste juste sur n'importe quel écran : c'est
          elle qui sert à reporter une distance. La planche délivrée est
          composée par le service, à l'échelle vraie. */}
      <div className="reperes-barre">
        <div className="reperes-barre-trait" style={{ width: `${Math.round(barre.pixels)}px` }} />
        <span className="reperes-barre-texte">{formaterDistance(barre.metres)}</span>
      </div>

      {/* ── Outil, accrochage, mesure ─────────────────────────────────── */}
      <div className="reperes-ligne">
        <span className="reperes-cle">Outil</span>
        <span className="reperes-valeur">{outil ?? TIRET}</span>
        {outilMesure && !outilMesure.actif && (
          <button type="button" className="lien" onClick={() => outilMesure.demarrer('ligne')}
                  title="Mesurer une distance ou une surface, sans rien déposer">
            Mesurer
          </button>
        )}
        {outilMesure && outilMesure.actif && (
          <span className="reperes-mesure-gestes" role="group" aria-label="Ce qu’on mesure">
            {(['ligne', 'surface'] as const).map((n) => (
              <button key={n} type="button" className="lien"
                      aria-pressed={outilMesure.nature === n}
                      onClick={() => outilMesure.demarrer(n)}>
                {n === 'ligne' ? 'Distance' : 'Surface'}
              </button>
            ))}
            <button type="button" className="lien" onClick={() => outilMesure.arreter()}>
              Fermer <kbd>Échap</kbd>
            </button>
          </span>
        )}
      </div>
      <div className="reperes-ligne">
        <span className="reperes-cle">Accrochage</span>
        <span className="reperes-valeur">{accroche ?? TIRET}</span>
      </div>
      <div className="reperes-ligne">
        <span className="reperes-cle">Mesure</span>
        <span className="reperes-valeur">{mesure ?? TIRET}</span>
      </div>

      {/* ── Sélection et opposabilité ──────────────────────────────────
          ⚠️ L'OPPOSABILITÉ EST CELLE DE L'OBJET SÉLECTIONNÉ, jamais un état
          global du plan — PC§35.4 : la géométrie cadastrale et la limite
          opposable ne se confondent pas. Rien de sélectionné : tiret. */}
      <div className="reperes-ligne">
        <span className="reperes-cle">Sélection</span>
        <span className="reperes-valeur">{selection ?? TIRET}</span>
        {actionSelection && (
          <button type="button" className="lien" onClick={actionSelection.executer}>
            {actionSelection.libelle}
          </button>
        )}
      </div>
      <div className="reperes-ligne">
        <span className="reperes-cle">Opposabilité</span>
        <span className="reperes-valeur">{opposabilite ?? TIRET}</span>
      </div>

      {/* ── Position — D.997 : elle s'affiche ICI, elle ne sélectionne rien ── */}
      <div className="reperes-ligne reperes-position">
        <span className="reperes-cle">Vous êtes sur</span>
        <span className="reperes-valeur">
          {position ? `${position.nom} (${position.echelon.replace(/_/g, '-')})` : TIRET}
        </span>
        {position && onAdopterPosition && (
          /* Adopter la position est le GESTE qui remplace l'ancien suivi
             automatique : un clic, et l'inspecteur ouvre cette collectivité. */
          <button type="button" className="lien" onClick={onAdopterPosition}>
            Ouvrir
          </button>
        )}
      </div>

      {/* ── La voie sous le curseur — l'info de la bulle, lisible ici aussi ── */}
      {voieSurvolee && (
        <div className="reperes-ligne">
          <span className="reperes-cle">Voie</span>
          <span className="reperes-valeur">{voieSurvolee}</span>
        </div>
      )}

      {/* ── Avertissements de seuils — ils expliquent une carte vide ─────
          ⚠️ Dire POURQUOI une couche est absente. Une carte sans parcelles à
          z10 n'est pas un territoire sans parcelles : c'est un seuil. Perdre
          ces mentions ferait relire « couche cassée » sur une carte juste. */}
      <div className="reperes-avertissements">
        {seuils && zoom < seuils.parcelles && (
          <span className="jeton avert">parcelles masquées sous z{seuils.parcelles}</span>
        )}
        {seuils && zoom < seuils.batiments && (
          <span className="jeton avert">bâti masqué sous z{seuils.batiments}</span>
        )}
        {zoomNomsDeRue !== undefined && zoom < zoomNomsDeRue && (
          <span className="jeton avert">noms de rue à partir de z{Math.round(zoomNomsDeRue)}</span>
        )}
        {zoomBatiComplet !== undefined && seuils && zoom >= seuils.batiments && zoom < zoomBatiComplet && (
          <span className="jeton avert">bâti rattaché seulement — tout le bâti à z{Math.round(zoomBatiComplet)}</span>
        )}
        {zoom >= 15 && inclinaison < 5 && (
          <span className="jeton avert">volumes du bâti : inclinez la vue (ctrl + glisser)</span>
        )}
      </div>

      {/* ⚠️ LE REFUS S'AFFICHE, IL NE SE JOURNALISE PAS. Un verrou qui ne s'est
          pas posé sans rien dire se lit comme un verrou posé. */}
      {refus && <p className="reperes-refus" role="alert">{refus}</p>}
    </footer>
  );
}
